// src/lib/syncController.ts
// ─────────────────────────────────────────────────────────────────────────────
// NIIZ ERP · Control de Sincronización en la Nube
// ─────────────────────────────────────────────────────────────────────────────
// Throttling + backoff exponencial + clasificación de errores de red/nube.
//
// Reglas:
//  1. Si no hay internet (navigator.onLine === false) NO se intenta sincronizar.
//  2. Ante un error de Supabase (429/rate limit, red o auth) se espera con
//     backoff exponencial: 10s → 30s → 1min → máx 5min.
//  3. La sincronización periódica de fondo corre como MÁXIMO 1 vez cada 2 minutos.
//  4. Nada de bucles de alta frecuencia ni de reintentos inmediatos: cada
//     intento agenda el siguiente con `setTimeout` según esta política.
//  5. Los errores se registran con límite de frecuencia (sin spam en consola).
//
// Actualización v110:
//   · Cero `any` — inspección tipada de errores (status/code/message)
//   · Backoff exponencial determinista y throttling anti-solapamiento
//   · Etiquetas legibles de error para UI/logs
//   · Self-test integrado sin dependencias externas
//   · 100% aditivo: preserva la API usada por trafficGovernor/cloudUploader
// ─────────────────────────────────────────────────────────────────────────────

/** Escalones del backoff exponencial (ms): 10s → 30s → 1min → 5min. */
export const BACKOFF_STEPS_MS = [10_000, 30_000, 60_000, 300_000] as const;

/** Tope máximo de espera entre reintentos. */
export const MAX_BACKOFF_MS = 300_000;

/** Máximo 1 sincronización periódica cada 2 minutos. */
export const MIN_BACKGROUND_SYNC_INTERVAL_MS = 120_000;

/** Los eventos internos (venta creada) no sincronizan más de 1 vez cada 15s. */
export const MIN_EVENT_SYNC_INTERVAL_MS = 15_000;

/** Ventana mínima entre mensajes de error en consola (anti-spam de memoria). */
const LOG_THROTTLE_MS = 30_000;

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export type SyncErrorKind = 'offline' | 'quota' | 'network' | 'auth' | 'schema' | 'other';

/** Etiquetas legibles para logs/UI. */
export const SYNC_ERROR_KIND_LABELS: Record<SyncErrorKind, string> = {
  offline: 'Sin conexión',
  quota: 'Límite de solicitudes',
  network: 'Error de red',
  auth: 'Error de autenticación',
  schema: 'Error de esquema',
  other: 'Error desconocido',
};

/** Retorna la etiqueta legible de un tipo de error de sincronización. */
export const syncErrorKindLabel = (kind: SyncErrorKind): string =>
  SYNC_ERROR_KIND_LABELS[kind] ?? kind;

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES DE INSPECCIÓN DE ERRORES (sin `any`)
// ─────────────────────────────────────────────────────────────────────────────

type UnknownRecord = Record<string, unknown>;

/** ¿Hay internet ahora mismo? */
export const isOnlineNow = (): boolean =>
  typeof navigator === 'undefined' ? true : navigator.onLine !== false;

const asRecord = (value: unknown): UnknownRecord | null =>
  value && typeof value === 'object' ? (value as UnknownRecord) : null;

const pickString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (value instanceof Error) return value.message || '';
  return '';
};

const messageOf = (err: unknown): string => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || '';

  const record = asRecord(err);
  if (!record) return '';

  return (
    pickString(record['message']) ||
    pickString(record['error_description']) ||
    pickString(record['details']) ||
    pickString(record['statusText']) ||
    ''
  );
};

const statusOf = (err: unknown): number | null => {
  const record = asRecord(err);
  if (!record) return null;

  const candidates = [record['status'], record['statusCode'], record['code']];

  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }

    if (typeof candidate === 'string' && candidate.trim() !== '') {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// CLASIFICACIÓN DE ERRORES
// ─────────────────────────────────────────────────────────────────────────────

/** Clasifica el error para decidir el backoff y si conviene seguir intentando. */
export const classifySyncError = (err: unknown): SyncErrorKind => {
  if (!isOnlineNow()) return 'offline';

  const status = statusOf(err);
  const msg = messageOf(err).toLowerCase();

  if (
    status === 429 ||
    msg.includes('429') ||
    msg.includes('too many requests') ||
    msg.includes('rate limit')
  ) {
    return 'quota';
  }

  if (
    status === 401 ||
    status === 403 ||
    msg.includes('jwt') ||
    msg.includes('invalid api key') ||
    msg.includes('unauthorized')
  ) {
    return 'auth';
  }

  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('timeout') ||
    msg.includes('temporarily unavailable') ||
    msg.includes('fetch failed') ||
    (status !== null && status >= 500)
  ) {
    return 'network';
  }

  if (msg.includes('could not find the') && msg.includes('column')) {
    return 'schema';
  }

  return 'other';
};

/** Errores que NO deben reintentarse inmediatamente (se sigue con backoff largo). */
export const shouldStopBatch = (kind: SyncErrorKind): boolean =>
  kind === 'quota' || kind === 'network' || kind === 'auth' || kind === 'offline';

/** Espera del backoff para el intento N (1 = primer fallo): 10s, 30s, 1min, 5min. */
export const backoffDelay = (attempt: number): number => {
  const normalized = Math.min(
    Math.max(1, Math.floor(attempt || 1)),
    BACKOFF_STEPS_MS.length,
  );

  return BACKOFF_STEPS_MS[normalized - 1];
};

// ─────────────────────────────────────────────────────────────────────────────
// THROTTLE DE SINCRONIZACIÓN
// ─────────────────────────────────────────────────────────────────────────────

export interface SyncThrottleOptions {
  minIntervalMs?: number;
  maxBackoffMs?: number;
}

export interface SyncThrottleStatus {
  attempts: number;
  blockedUntil: number;
  lastRunAt: number;
  inFlight: boolean;
  nextAllowedInMs: number;
}

/**
 * Política única de sincronización (una instancia por proceso).
 * Evita solapamientos, respeta el backoff y limita la frecuencia periódica.
 */
export class SyncThrottle {
  private failuresCount = 0;
  private blockedUntil = 0;
  private lastRunAt = 0;
  private inFlight = false;
  private readonly minIntervalMs: number;
  private readonly maxBackoffMs: number;

  constructor(options: SyncThrottleOptions = {}) {
    this.minIntervalMs = options.minIntervalMs ?? MIN_BACKGROUND_SYNC_INTERVAL_MS;
    this.maxBackoffMs = options.maxBackoffMs ?? MAX_BACKOFF_MS;
  }

  /** Intentos fallidos consecutivos. */
  get attempts(): number {
    return this.failuresCount;
  }

  /** Milisegundos restantes del backoff (0 si ya se puede reintentar). */
  get backoffRemaining(): number {
    return Math.max(0, this.blockedUntil - Date.now());
  }

  get isRunning(): boolean {
    return this.inFlight;
  }

  /** Timestamp del último intento (0 si nunca corrió). Lectura pública para tests/UI. */
  get lastRunTime(): number {
    return this.lastRunAt;
  }

  /** ¿Falta menos de la frecuencia mínima o está en backoff? */
  nextAllowedIn(): number {
    const byBackoff = this.backoffRemaining;
    const byInterval = Math.max(0, this.minIntervalMs - (Date.now() - this.lastRunAt));
    return Math.max(byBackoff, byInterval);
  }

  /**
   * ¿Se puede sincronizar ahora?
   * @param manual los clicks del usuario omiten la espera periódica, NUNCA el backoff.
   */
  canRun(options: { manual?: boolean } = {}): boolean {
    if (this.inFlight) return false;
    if (this.backoffRemaining > 0) return false;
    if (options.manual) return true;
    return Date.now() - this.lastRunAt >= this.minIntervalMs;
  }

  /** Marca el inicio de un intento (evita solapamientos). */
  markRun(): void {
    this.inFlight = true;
    this.lastRunAt = Date.now();
  }

  finishRun(): void {
    this.inFlight = false;
  }

  /** Éxito: se limpia el backoff. */
  onSuccess(): void {
    this.failuresCount = 0;
    this.blockedUntil = 0;
  }

  /** Fallo: se incrementa el intento y se bloquea hasta el próximo escalón. */
  onFailure(err: unknown): { delay: number; kind: SyncErrorKind } {
    this.failuresCount += 1;
    const kind = classifySyncError(err);
    const delay = Math.min(backoffDelay(this.failuresCount), this.maxBackoffMs);
    this.blockedUntil = Date.now() + delay;
    return { delay, kind };
  }

  reset(): void {
    this.failuresCount = 0;
    this.blockedUntil = 0;
    this.lastRunAt = 0;
    this.inFlight = false;
  }

  /** Snapshot del estado interno (útil para debugging/UI). */
  getStatus(): SyncThrottleStatus {
    return {
      attempts: this.failuresCount,
      blockedUntil: this.blockedUntil,
      lastRunAt: this.lastRunAt,
      inFlight: this.inFlight,
      nextAllowedInMs: this.nextAllowedIn(),
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// LOG ANTI-SPAM (no saturar el historial/memoria del navegador)
// ─────────────────────────────────────────────────────────────────────────────

let lastLogAt = 0;
let suppressedLogs = 0;

/** Cuántos avisos se han suprimido desde el último log emitido. */
export const getSuppressedLogCount = (): number => suppressedLogs;

/** Resetea el contador de logs suprimidos (útil para tests). */
export const resetSyncLogger = (): void => {
  lastLogAt = 0;
  suppressedLogs = 0;
};

/**
 * Registra un aviso como máximo una vez cada 30s (y resume los omitidos).
 * Se usa `console.warn` en lugar de `console.error` masivo.
 */
export const logSyncIssue = (message: string, detail?: unknown): void => {
  const now = Date.now();

  if (now - lastLogAt < LOG_THROTTLE_MS) {
    suppressedLogs += 1;
    return;
  }

  lastLogAt = now;
  const suppressed = suppressedLogs > 0 ? ` (+${suppressedLogs} avisos similares omitidos)` : '';
  suppressedLogs = 0;

  console.warn(`[sync] ${message}${suppressed}`, detail ?? '');
};

/** Solo en desarrollo, con la misma limitación de frecuencia. */
export const logSyncDebug = (message: string, detail?: unknown): void => {
  if (typeof import.meta === 'undefined' || !import.meta.env?.DEV) return;
  logSyncIssue(message, detail);
};

// ─────────────────────────────────────────────────────────────────────────────
// SELF-TEST INTEGRADO
// ─────────────────────────────────────────────────────────────────────────────

export function runSyncControllerSelfTest(): number {
  let passed = 0;

  const assert = (cond: boolean, label: string): void => {
    if (!cond) throw new Error(`[syncController SELF-TEST FAIL] ${label}`);
    passed += 1;
  };

  // ── Backoff determinista ──
  assert(backoffDelay(0) === 10_000, 'backoffDelay(0) usa el primer escalón');
  assert(backoffDelay(1) === 10_000, 'backoffDelay(1) = 10s');
  assert(backoffDelay(2) === 30_000, 'backoffDelay(2) = 30s');
  assert(backoffDelay(3) === 60_000, 'backoffDelay(3) = 1min');
  assert(backoffDelay(4) === 300_000, 'backoffDelay(4) = 5min');
  assert(backoffDelay(99) === 300_000, 'backoffDelay(99) se topa en 5min');

  // ── Política de reintento ──
  assert(shouldStopBatch('quota') === true, 'quota detiene el batch');
  assert(shouldStopBatch('network') === true, 'network detiene el batch');
  assert(shouldStopBatch('auth') === true, 'auth detiene el batch');
  assert(shouldStopBatch('offline') === true, 'offline detiene el batch');
  assert(shouldStopBatch('other') === false, 'other permite continuar');
  assert(shouldStopBatch('schema') === false, 'schema permite continuar');

  // ── Clasificación de errores ──
  const online = isOnlineNow();

  if (online) {
    assert(classifySyncError({ status: 429 }) === 'quota', '429 → quota');
    assert(classifySyncError({ message: 'Too Many Requests' }) === 'quota', 'Too Many Requests → quota');
    assert(classifySyncError({ message: 'rate limit exceeded' }) === 'quota', 'rate limit → quota');

    assert(classifySyncError({ status: 401 }) === 'auth', '401 → auth');
    assert(classifySyncError({ status: 403 }) === 'auth', '403 → auth');
    assert(classifySyncError({ message: 'JWT expired' }) === 'auth', 'JWT → auth');
    assert(classifySyncError({ message: 'Invalid API key' }) === 'auth', 'Invalid API key → auth');

    assert(classifySyncError({ message: 'Failed to fetch' }) === 'network', 'Failed to fetch → network');
    assert(classifySyncError({ message: 'NetworkError' }) === 'network', 'NetworkError → network');
    assert(classifySyncError({ message: 'timeout' }) === 'network', 'timeout → network');
    assert(classifySyncError({ status: 503 }) === 'network', '503 → network');

    assert(
      classifySyncError({ message: 'could not find the column foo' }) === 'schema',
      'column missing → schema',
    );

    assert(classifySyncError({ message: 'weird error' }) === 'other', 'error desconocido → other');
  } else {
    assert(classifySyncError({ status: 429 }) === 'offline', 'sin conexión → offline');
    assert(classifySyncError({ message: 'Failed to fetch' }) === 'offline', 'sin conexión → offline');
  }

  // ── SyncThrottle ──
  const throttle = new SyncThrottle({ minIntervalMs: 1_000, maxBackoffMs: 2_000 });

  assert(throttle.attempts === 0, 'throttle inicia sin intentos');
  assert(throttle.canRun({ manual: true }) === true, 'manual puede correr al inicio');
  assert(throttle.canRun() === true, 'background puede correr al inicio');

  throttle.markRun();
  assert(throttle.isRunning === true, 'markRun pone inFlight');
  assert(throttle.canRun({ manual: true }) === false, 'no se solapan runs');
  assert(throttle.canRun() === false, 'no se solapan runs (background)');

  throttle.finishRun();
  assert(throttle.isRunning === false, 'finishRun libera inFlight');
  assert(throttle.canRun({ manual: true }) === true, 'manual ignora intervalo mínimo');
  assert(throttle.canRun() === false, 'background respeta intervalo mínimo');

  const failure = throttle.onFailure({ status: 429 });
  assert(throttle.attempts === 1, 'onFailure incrementa intentos');
  assert(failure.delay === 2_000, 'onFailure respeta maxBackoffMs');
  assert(throttle.canRun({ manual: true }) === false, 'backoff bloquea incluso manual');
  assert(throttle.canRun() === false, 'backoff bloquea background');

  throttle.onSuccess();
  assert(throttle.attempts === 0, 'onSuccess limpia intentos');
  assert(throttle.backoffRemaining === 0, 'onSuccess limpia backoff');
  assert(throttle.canRun({ manual: true }) === true, 'tras éxito manual vuelve a poder correr');

  const status = throttle.getStatus();
  assert(status.attempts === 0, 'getStatus refleja attempts');
  assert(status.inFlight === false, 'getStatus refleja inFlight');
  assert(status.nextAllowedInMs >= 0, 'getStatus nextAllowedInMs no negativo');

  throttle.reset();
  assert(throttle.attempts === 0, 'reset limpia attempts');
  assert(throttle.lastRunTime === 0, 'reset limpia lastRunAt');

  // ── Logger anti-spam ──
  const originalWarn = console.warn;
  const warnings: string[] = [];

  console.warn = ((...args: unknown[]) => {
    warnings.push(String(args[0] ?? ''));
  }) as typeof console.warn;

  try {
    resetSyncLogger();

    logSyncIssue('primero');
    logSyncIssue('segundo');
    logSyncIssue('tercero');

    assert(warnings.length === 1, 'logSyncIssue solo emite 1 aviso en la ventana');
    assert(getSuppressedLogCount() === 2, 'logSyncIssue cuenta los omitidos');
    assert(warnings[0].includes('[sync] primero'), 'primer aviso emitido correctamente');

    resetSyncLogger();
    assert(getSuppressedLogCount() === 0, 'resetSyncLogger limpia suprimidos');
  } finally {
    console.warn = originalWarn;
    resetSyncLogger();
  }

  return passed;
}

// ─── Auto-ejecución en desarrollo ───
if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
  try {
    const count = runSyncControllerSelfTest();
    console.log(`✅ [syncController.ts] ${count} aserciones internas verificadas.`);
  } catch (err) {
    console.error('🔴 [syncController.ts] FALLO en pruebas internas:', err);
  }
}